import { Command, useCommandState } from "cmdk";
import { CircularProgressCenter } from "igfd-ui";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Box, Modal, Popover, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { MonoChip, Pill } from "igfd-ui/src/admin";

import { CheckIcon } from "@dashboard/components/common/icons";
import type { RestaurantEnv } from "@dashboard/types";

import { envTone } from "../workspaces/workspaceEnv";
import { workspaceSecondaryLine } from "./workspaceDisplay";
import { buildWorkspaceSections } from "./workspaceSections";
import type { WorkspaceOption } from "./workspaceSections";

const EnvPill = ({ env }: { env: RestaurantEnv }) => (
  <Pill tone={envTone(env)}>{env}</Pill>
);

const WorkspaceOptionRow = ({
  option,
  showIds,
  onSelect,
}: {
  option: WorkspaceOption;
  showIds: boolean;
  onSelect: () => void;
}) => (
  <Box
    component={Command.Item}
    // Name first for scoring; uuid keeps the value unique.
    value={`${option.name} ${option.uuid}`}
    onSelect={onSelect}
    sx={({ palette }) => ({
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "10px 12px",
      borderRadius: "8px",
      cursor: "pointer",
      '&[data-selected="true"]': { backgroundColor: palette.grey[300] },
    })}
  >
    <Box
      sx={{
        minWidth: 0,
        flex: 1,
        display: "flex",
        alignItems: "baseline",
        gap: "8px",
      }}
    >
      <Typography
        variant="bodyRRegular"
        component="span"
        sx={({ palette }) => ({
          minWidth: 0,
          fontWeight: 500,
          color: palette.grey[900],
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        })}
      >
        {option.name}
      </Typography>
      {workspaceSecondaryLine(option) && (
        <Typography
          variant="bodyS"
          component="span"
          sx={({ palette }) => ({
            flexShrink: 0,
            color: palette.grey[700],
            whiteSpace: "nowrap",
          })}
        >
          {workspaceSecondaryLine(option)}
        </Typography>
      )}
    </Box>
    {showIds && (
      // Don't select the row when copying the id.
      <Box
        component="span"
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        sx={{ display: "inline-flex" }}
      >
        <MonoChip
          value={option.uuid}
          display={option.uuid.slice(0, 8)}
          withIcon
        />
      </Box>
    )}
    <EnvPill env={option.env} />
  </Box>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    aria-hidden
    variant="labelSMedium"
    sx={({ palette }) => ({
      display: "block",
      padding: "18px 12px 6px",
      color: palette.grey[700],
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    })}
  >
    {children}
  </Typography>
);

const CurrentWorkspaceBlock = ({
  option,
  showIds,
}: {
  option: WorkspaceOption;
  showIds: boolean;
}) => (
  <Box>
    <SectionHeading>Current</SectionHeading>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 12px",
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          alignItems: "baseline",
          gap: "8px",
        }}
      >
        <Typography
          variant="bodyRRegular"
          component="span"
          sx={({ palette }) => ({
            minWidth: 0,
            fontWeight: 500,
            color: palette.grey[900],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {option.name}
        </Typography>
        {workspaceSecondaryLine(option) && (
          <Typography
            variant="bodyS"
            component="span"
            sx={({ palette }) => ({
              flexShrink: 0,
              color: palette.grey[700],
              whiteSpace: "nowrap",
            })}
          >
            {workspaceSecondaryLine(option)}
          </Typography>
        )}
      </Box>
      {showIds && (
        <MonoChip
          value={option.uuid}
          display={option.uuid.slice(0, 8)}
          withIcon
        />
      )}
      <CheckIcon
        size={18}
        sx={({ palette }) => ({ color: palette.grey[700] })}
      />
      <EnvPill env={option.env} />
    </Box>
  </Box>
);

const ResultCountAnnouncement = () => {
  const resultCount = useCommandState((state) => state.filtered.count);
  return (
    <Typography
      aria-live="polite"
      sx={{ position: "absolute", left: "-9999px" }}
    >
      {resultCount === 0
        ? "No workspaces found"
        : `${resultCount} workspace${resultCount === 1 ? "" : "s"}`}
    </Typography>
  );
};

export const WorkspaceSwitcherView = ({
  open,
  onClose,
  anchorEl,
  options,
  currentWorkspaceUuid,
  loading,
  error,
  showIds,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  options: WorkspaceOption[];
  currentWorkspaceUuid: string | undefined;
  loading: boolean;
  error: boolean;
  showIds: boolean;
  onSelect: (option: WorkspaceOption) => void;
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { current, sections, flatOptions } = useMemo(
    () => buildWorkspaceSections({ options, currentWorkspaceUuid }),
    [options, currentWorkspaceUuid]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setQuery("");
    // Focus after the Modal/Popover focus trap settles.
    const focusFrame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(focusFrame);
  }, [open]);

  const body = (
    <Box
      component={Command}
      label="Switch workspace"
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Typography
        id="workspace-switcher-title"
        sx={{ position: "absolute", left: "-9999px" }}
      >
        Switch workspace
      </Typography>
      <Box
        sx={({ palette }) => ({
          padding: "16px 18px",
          borderBottom: `1px solid ${palette.grey[300]}`,
        })}
      >
        <Box
          component={Command.Input}
          ref={inputRef}
          placeholder="Switch workspace…"
          value={query}
          onValueChange={setQuery}
          sx={({ palette }) => ({
            width: "100%",
            border: "none",
            outline: "none",
            backgroundColor: "transparent",
            // <16px fonts make iOS Safari zoom on focus.
            fontSize: "16px",
            "@media (pointer: fine)": { fontSize: "14px" },
            color: palette.grey[900],
            "&::placeholder": { color: palette.grey[700] },
          })}
        />
      </Box>

      {loading ? (
        <CircularProgressCenter fullScreen={false} />
      ) : error ? (
        <Typography
          variant="bodyS"
          sx={({ palette }) => ({
            padding: "16px 12px",
            color: palette.grey[700],
          })}
        >
          Couldn&apos;t load workspaces. Refresh to try again.
        </Typography>
      ) : (
        <Box
          component={Command.List}
          label="Workspaces"
          sx={{ overflowY: "auto", padding: "6px" }}
        >
          {query ? (
            // cmdk ranks globally only for ungrouped direct children of the list.
            <>
              <SectionHeading>Results</SectionHeading>
              {flatOptions.map((option) => (
                <WorkspaceOptionRow
                  key={option.uuid}
                  option={option}
                  showIds={showIds}
                  onSelect={() => onSelect(option)}
                />
              ))}
            </>
          ) : (
            <>
              {current && (
                <CurrentWorkspaceBlock option={current} showIds={showIds} />
              )}
              {sections.map((section) => (
                <Command.Group
                  key={section.key}
                  heading={<SectionHeading>{section.heading}</SectionHeading>}
                >
                  {section.options.map((option) => (
                    <WorkspaceOptionRow
                      key={option.uuid}
                      option={option}
                      showIds={showIds}
                      onSelect={() => onSelect(option)}
                    />
                  ))}
                </Command.Group>
              ))}
            </>
          )}

          <Command.Empty>
            <Typography
              variant="bodyS"
              component="span"
              sx={({ palette }) => ({
                display: "block",
                padding: "16px 12px",
                color: palette.grey[700],
              })}
            >
              No workspaces found
            </Typography>
          </Command.Empty>
        </Box>
      )}

      <ResultCountAnnouncement />
    </Box>
  );

  if (anchorEl) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        aria-labelledby="workspace-switcher-title"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={8}
        PaperProps={{
          sx: (theme) => ({
            width: "340px",
            maxHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.common.white,
            borderRadius: "24px",
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: theme.shadows[6],
            overflow: "hidden",
          }),
        }}
      >
        {body}
      </Popover>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="workspace-switcher-title"
      slotProps={{
        backdrop: {
          sx: ({ palette }) => ({
            backgroundColor: alpha(palette.grey[900], 0.5),
          }),
        },
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      <Box
        sx={(theme) => ({
          marginTop: "12vh",
          width: "600px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.common.white,
          borderRadius: "24px",
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: theme.shadows[6],
          outline: "none",
          overflow: "hidden",
        })}
      >
        {body}
      </Box>
    </Modal>
  );
};
